import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseOutlined } from '@ant-design/icons';
import { Radio, Button, message } from 'antd';
import SelectPropertyByProperty from '~components/special-offer/map/offer-map-by-property/property-list-editor/components/select-property-by-property';
import LandlordSelector from '~components/special-offer/map/offer-map-by-property/property-list-editor/components/select-property-by-landlord/landlord-selector';
import PropertyReviewList from '~components/special-offer/map/offer-map-by-property/property-list-editor/components/select-property-by-landlord/property-review-list';
import { useMutation } from '@apollo/client';
import { CMS_MUTATION_LINK_SPECIAL_OFFER } from './queries';

export default function PropertyListEditorContent(props) {
  const { onClose, onSuccess, specialOfferId } = props;
  const { t } = useTranslation();

  const [selectedPropertySelector, setSelectedPropertySelector] = useState('Property');
  const [selectPropertyByLandlordStep, setSelectPropertyByLandlordStep] =
    useState('SelectLandlord');

  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);

  const [selectedLandlordIds, setSelectLandlordIds] = useState([]);
  const [landlordSelectorParams, setLandlordSelectorParams] = useState({
    pageNumber: 1,
    selectedLandlordList: [],
    landlordList: [],
  });
  const [excludePropertyIds, setExcludePropertyIds] = useState([]);

  const [linkSpecialOffer, { loading: linkSpecialOfferLoading }] = useMutation(
    CMS_MUTATION_LINK_SPECIAL_OFFER,
    {
      onCompleted: () => {
        message.success(t('Add property into special offer success'));
        onSuccess();
        onClose();
      },
      onError: e => {
        if (e) {
          message.error(e.toString());
        } else {
          message.error(t('Something wrong, please try again.'));
        }
      },
    },
  );

  const onContinue = async () => {
    if (selectedPropertySelector === 'Property') {
      //call API
      await linkSpecialOffer({
        variables: {
          input: {
            id: specialOfferId,
            propertyIds: selectedPropertyIds,
          },
        },
      });
    }

    if (selectedPropertySelector === 'Landlord') {
      if (selectPropertyByLandlordStep === 'SelectLandlord') {
        setSelectPropertyByLandlordStep('SelectProperty');
      } else {
        //call API
        await linkSpecialOffer({
          variables: {
            input: {
              id: specialOfferId,
              excludePropertyIds: excludePropertyIds,
              landlordIds: selectedLandlordIds,
            },
          },
        });
      }
    }
  };

  const onBack = () => {
    if (selectedPropertySelector === 'Property') {
      onClose();
    }

    if (selectedPropertySelector === 'Landlord') {
      if (selectPropertyByLandlordStep === 'SelectLandlord') {
        onClose();
      } else {
        setSelectPropertyByLandlordStep('SelectLandlord');
      }
    }
  };

  const continueButtonDisabled = useMemo(() => {
    if (selectedPropertySelector === 'Property') {
      return selectedPropertyIds.length === 0;
    } else {
      return selectedLandlordIds.length === 0;
    }
  }, [selectedPropertySelector, selectedPropertyIds, selectedLandlordIds]);

  return (
    <div className="property-list-editor-container">
      <div className="property-list-editor-container__header">
        {t('cms.table.header.button.add_new_property')}
        <CloseOutlined className="property-list-editor-container__close-button" onClick={onClose} />
      </div>
      <div className="property-list-editor-container__body">
        <If condition={selectPropertyByLandlordStep === 'SelectLandlord'}>
          <section className="new-property-selector-radio">
            <h5>{t('cms.table.header.button.add_new_property_by') + ':'}</h5>
            <Radio.Group
              onChange={e => setSelectedPropertySelector(e.target.value)}
              value={selectedPropertySelector}
            >
              <Radio value="Property">{t('cms.special_offer.map.modal.radio_type.property')}</Radio>
              <Radio value="Landlord">{t('cms.special_offer.map.modal.radio_type.landlord')}</Radio>
            </Radio.Group>
          </section>
        </If>

        <Choose>
          <When condition={selectedPropertySelector === 'Property'}>
            <SelectPropertyByProperty
              selected={selectedPropertyIds}
              onChange={v => setSelectedPropertyIds(v)}
            />
          </When>
          <When condition={selectedPropertySelector === 'Landlord'}>
            <Choose>
              <When condition={selectPropertyByLandlordStep === 'SelectLandlord'}>
                <LandlordSelector
                  onConfirm={({ ids, selectedLandlordList, currentPageNumber }) => {
                    setSelectLandlordIds(ids);
                    setLandlordSelectorParams({
                      selectedLandlordList,
                      currentPageNumber,
                    });
                  }}
                  value={selectedLandlordIds}
                  defaultQueryParams={landlordSelectorParams}
                />
              </When>
              <When condition={selectPropertyByLandlordStep === 'SelectProperty'}>
                <PropertyReviewList
                  landlordIds={selectedLandlordIds}
                  excludePropertyIds={excludePropertyIds}
                  setExcludePropertyIds={setExcludePropertyIds}
                />
              </When>
            </Choose>
          </When>
        </Choose>
      </div>
      <div className="property-list-editor-container__footer">
        <Button
          ghost
          type="primary"
          size="large"
          className="property-list-editor-container__cancel-button"
          onClick={onBack}
        >
          <Choose>
            <When
              condition={
                selectedPropertySelector === 'Landlord' &&
                selectPropertyByLandlordStep === 'SelectProperty'
              }
            >
              {t('cms.special_offer.back.btn')}
            </When>
            <Otherwise>{t('cms.referral.commission_plan.modal.btn.cancel')}</Otherwise>
          </Choose>
        </Button>

        <Button
          type="primary"
          size="large"
          className="property-list-editor-container__save-button"
          loading={linkSpecialOfferLoading}
          onClick={onContinue}
          disabled={continueButtonDisabled}
        >
          <Choose>
            <When
              condition={
                selectedPropertySelector === 'Landlord' &&
                selectPropertyByLandlordStep === 'SelectLandlord'
              }
            >
              {t('cms.sepcial_offer.continue.btn')}
            </When>
            <Otherwise>{t('cms.special_offer.confirm.btn')}</Otherwise>
          </Choose>
        </Button>
      </div>
    </div>
  );
}
